import React, { Fragment } from 'react';
import { withCookies } from 'react-cookie';
import { withRouter } from 'react-router-dom';
import { Typography, Button, TextField, Checkbox, Grid } from '@material-ui/core';

import { RestComponent, Currency, DateUtils, ManageTextField, ManageNumericField, Email, RichTextDisplay, draftContentHasText } from 'react-frontend-utils' 

import { ThemeColors } from '../Theme'
import { Global, Constructors } from '../models/Global'
import { Patron } from '../models/Patron';
import { MultiDayEvent } from '../models/MultiDayEvent'

import PatronCheckout from '../components/PatronCheckout'
import { CommonPatronFunctions } from '../components/CommonPatronFunctions';

import { renderField, Field } from 'form-builder'

/**
 * Page 2 of the Event Signup process. Displays the event details and allows the user to signup
 * 
 * The following props are required:
 * - eventId: the Event id the user is signing up for
 * - agsServiceFee: the AGS service fee for the booking {serviceFeeMultiplier, serviceFeeFixed}
 * - prevPageCallback: a callback that returns to the previous page
 * - nextPageCallback: a callback that is called when the user has confirmed and paid for the Event
 * 
 */
export class EventSignupPage2 extends RestComponent {

    styles = {
        roundedContainer: {
            border: '1px solid #CCCCCC', 
            borderRadius: '4px', 
            padding: 10,
            marginBottom: 20,
        },
        sectionLabel: {
            fontSize: 16, 
            color: 'black', 
            marginLeft: 5
        },
        membershipInstructionsLabel: {
            color: 'gray',
            fontSize: '10pt',
            flexGrow: 1,
            fontStyle: 'italic',
            textAlign: 'left'
        },
        membershipField: {
            marginTop: 5,
            marginBottom: 5
        },
    }

    _prevPageCallback;
    _nextPageCallback;
    _timezone = Global.getTimezone();
    
    _patron = new Patron();     // Create a new Patron object to store the user's information, will use the same id each time a signup is attempted

    constructor(props) {
        super(props);
    
        this._prevPageCallback = props.prevPageCallback;  //callback that returns to EventSignupPage1
        this._nextPageCallback = props.nextPageCallback;

        this.state.event = null;
        this.state.multiDaySlaves = null;   // If this is a multiday event, the list of slave events in the series

        this.state.optionalCosts = [];
        this.state.patronCount = 1;
        this.state.patronNames = [""];
        this.state.patronEmail = "";
        this.state.patronPhone = "";
        this.state.contactName = "";    
        this.state.enableSms = false;
        this.state.tcAgree = false;

        this.state.clientSecret = null;
        this.state.membershipInfo = "";   //The membership ID or barcode typed in to identify the Membership
    }
    
    
    componentDidMount() {
        super.componentDidMount(); 
        
        this._fetchEvent(this.props.eventId);

        // Browser back button - go back to Application List
        window.addEventListener("popstate", this._prevPageCallback);
    }
   
    componentWillUnmount() {
        super.componentWillUnmount();       
        window.removeEventListener("popstate", this._prevPageCallback);
    }

    _fetchEvent = (eventId) => {
        this.incrementBusy();
        this.secureJSONFetch("/patron/events/" + eventId + "/signup", {method: 'GET'}, 
                             (response) => this._fetchEventComplete(response), this._fetchErrorCallback); 
    }

    _fetchEventComplete = (response) => {
        this.decrementBusy();
        if (response) {
            const ev = Constructors.slotFromJson(response, this._timezone);
            const opCosts = CommonPatronFunctions.createOptionalCostsState(ev.signup.optionalCosts.length);
            this.setState({event: ev, optionalCosts: opCosts});

            const isMultiday = ev instanceof MultiDayEvent;
            if (isMultiday) {
                this.incrementBusy();
                this.secureJSONFetch("/patron/events/multiday/" + ev.masterEventId, {method: 'GET'}, 
                             (response) => this._fetchMultiDaySeriesComplete(response, ev), this._fetchErrorCallback); 
            }

            const hasStarted = ev.hasStarted();
            const hasFinished = ev.hasFinished();
            if (hasStarted && !isMultiday) {
                const title = hasFinished ? "Event Has Finished" : "Event In Progress";
                let msg;
                if (!hasFinished)
                    msg = "This Event has already started and is in progress.";
                else
                    msg = "This Event is in the past and has already finished.";
                
                this.showConfirmAlert(title, msg, 'red', "Ok");
            }
        }
    }

    _fetchMultiDaySeriesComplete = (response, master) => {
        this.decrementBusy();
        if (response) {
            const seriesEvents = response.map(json => Constructors.slotFromJson(json, this._timezone));

            // remove the master event and any cancelled slave events from the list
            const activeSlaveEvents = seriesEvents.filter(me => !me.isMaster() && me.state === "ACTIVE");
            
            // The series is in progress if the master has started and the last session has not yet finished
            if (master.hasStarted()) {
                const lastSession = activeSlaveEvents.reduce((last, e) => e.end > last.end ? e : last, master);
                const finished = lastSession.hasFinished();
                const title = finished ? "Multi-Day Event Has Finished" : "Multi-Day Event In Progress";
                const msg = finished ? "This Multi-Day Event is in the past and all sessions are finished"
                                     : "This Multi-Day Event is in progress. Some sessions may have finished or be in progress.";
                this.showConfirmAlert(title, msg, 'red', "Ok");
            }

            this.setState({multiDaySlaves: activeSlaveEvents});
        }
    }


    _patronCountChanged = (json, value) => {
        this.setState({patronCount: value});

        // If the patron count changes, we need add or remove patron names
        const patronNames = [...this.state.patronNames];
        while (patronNames.length < value)
            patronNames.push("");
        while (patronNames.length > value)
            patronNames.pop();
        this.setState({patronNames: patronNames});
    }
    
    _setPatronEmail = (json, userValue) => {
        if (!Email.validateEmail(userValue)) {
            this.showConfirmAlert("Invalid Email Address", "Invalid email address (check for whitespaces, invalid characters, and valid domain)", 'red');
            return;
        }
        this.setState({patronEmail: userValue});
    }

    _setPatronName = (json, userValue) => { 
        this.setState({patronName: userValue});
    }

    _setPatronPhone = (json, userValue) => {
        this.setState({patronPhone: userValue});
    }

    _setContactName = (json, userValue) => {
        this.setState({contactName: userValue});
    }

    _submit = (grandTotal, payInCash) => {

        if (this.state.event.signup.fieldTree) {

            if (Field.fieldMissingData(this.state.event.signup.fieldTree) ) {
                this.showConfirmAlert("Missing Question Data", "One or more questions require an answer", 'red');
                return;
            }

            if (Field.fieldErrored(this.state.event.signup.fieldTree) ) {
                this.showConfirmAlert("Invalid Question Data", "One or more questions have an error", 'red');
                return;
            }
        }

        if (payInCash) {
            this.showConfirmAlert("Pay for Event Later", "Are you sure you want to pay for this event later with one of the other payment methods provided by your Community?", 'black',
                "No", 
                () => this._continueSubmit(grandTotal, payInCash),
                "Yes",
                'green');
        }
        else
            this._continueSubmit(grandTotal, payInCash);
    }



    _continueSubmit = (grandTotal, payInCash) => {

        const event = this.state.event;
        this._patron.clear();

        CommonPatronFunctions.setOptionalCostsOnPatron(this._patron, this.state.optionalCosts, event.signup.optionalCosts);
       
        this._patron.patronCount = this.state.patronCount;
        this._patron.patronNames = this.state.patronNames;
        if (this._patron.patronNames[0].trim().length === 0) {
            // Move the first patron name that's not empty to the first slot, or use the contact name if all are empty
                const firstNonEmptyIndex = this._patron.patronNames.findIndex(name => name.trim().length > 0);
                if (firstNonEmptyIndex > 0)
                    [this._patron.patronNames[0], this._patron.patronNames[firstNonEmptyIndex]] = [this._patron.patronNames[firstNonEmptyIndex], this._patron.patronNames[0]];
                else
                    this._patron.patronNames[0] = this.state.contactName;
        }
        this._patron.patronEmail = this.state.patronEmail;
        this._patron.patronPhone = this.state.patronPhone;
        this._patron.contactName = this.state.contactName;
        this._patron.enableSms = this.state.enableSms;

        this._patron.calculatedCost = grandTotal;
        this._patron.isoCurrency = event.signup.isoCurrency;
        this._patron.isCashPayment = payInCash;
        if (this.state.membershipInfo)
            this._patron.membershipInfo = this.state.membershipInfo;
       
        if (event.signup.fieldTree)
            this._patron.patronQuestions = {fieldTree: event.signup.fieldTree, type: "PatronQuestions"};

        const body = JSON.stringify(this._patron);

        const cashAmountDue = payInCash ? Currency.round(grandTotal) + " " + event.signup.isoCurrency : null;

        this.incrementBusy();
        this.secureJSONFetch("/patron/events/" + event.id + "/signup", {method: 'POST', body: body}, 
                             (response) => this._submitComplete(response, cashAmountDue), this._fetchErrorCallback); 
    }


    _gotoEventConfirmationPage = (cashAmountDue) => {
        this._nextPageCallback(this._patron.id, cashAmountDue, this.state.multiDaySlaves);
    }

    _submitComplete = (response, cashAmountDue) => {
        this.decrementBusy();
        if (response)  //response is a client secret if payment required, or null if not
            this.setState({clientSecret: response, showPayment: true});
        else
            this._gotoEventConfirmationPage(cashAmountDue);  // free or cash, we're done
    }


    _fetchErrorCallback = (error) => {
        this.showConfirmAlert("Error", error, 'red');
        this.decrementBusy();
    }


    //Patron cancels the payment
    _returnFromPayment = () => {
        this.setState({showPayment: false, clientSecret: null});
    }


    
    //Called when a field is updated or on first load.
    _additionalQuestionfieldChanged = (field, newValue) => {
        this.forceUpdate();
    }

    _additionalQuestionfieldInputError = (field, error) => {
        this.showConfirmAlert("Error in Field \"" + field + "\"", error, 'red');
        this.forceUpdate();
    }


    render() {

        const event = this.state.event;
        if (!event) {
            return <Fragment>
                        {this.getConfirmAlertComponent()}
                    </Fragment>;
        }

        const signup = event.signup;

        const msg = "Your signup slot is not reserved until you complete this form. If space is limited, it is possible for the Event to fill up before you complete this form.";

        const hasAbout = draftContentHasText(signup.about);
        
        const totalOptionalCosts = CommonPatronFunctions.getTotalOptionalCosts(this.state.optionalCosts, signup.optionalCosts);
        
        const patronCost = signup.cost * this.state.patronCount;
        const patronCostDescription = this.state.patronCount + " patron" + (this.state.patronCount > 1 ? "s" : "") + " x " + Currency.round(signup.cost) + " " + signup.isoCurrency + " per patron";

        const subtotal = patronCost + totalOptionalCosts;

        const totalServiceFees = CommonPatronFunctions.calculateServiceFee(subtotal, signup.serviceFeePercent,
                                                            signup.serviceFeeFixed, this.props.agsServiceFee);

        const grandTotal = subtotal + totalServiceFees;

        const hasTerms = draftContentHasText(signup.termsAndConditions);

        const remainingMinutes = event.remainingTimeMinutes();
        const remaininStr = remainingMinutes > 0 ? "Time Remaining to sign up: " + DateUtils.relativeTimeFriendlyFormat(remainingMinutes) : "";
        
        const patronsAddedByOptionalCost = CommonPatronFunctions.getTotalAddsToPatronCount(this.state.optionalCosts, signup.optionalCosts);

        let patronCountMatch = false;
        if (patronsAddedByOptionalCost == null)
            patronCountMatch = true;    // no optional cost adds to patron count, so we "say" it matches
        else if (patronsAddedByOptionalCost === this.state.patronCount)
            patronCountMatch = true;

        let patronExpectedStr = "";
        if (!patronCountMatch) {
            if (patronsAddedByOptionalCost < this.state.patronCount)
                patronExpectedStr = "Please add " + (this.state.patronCount - patronsAddedByOptionalCost) + " item" + ((this.state.patronCount - patronsAddedByOptionalCost) > 1 ? "s" : "") + " below to match the number of patrons attending.";
            else
                patronExpectedStr = "Please remove " + (patronsAddedByOptionalCost - this.state.patronCount) + " item" + ((patronsAddedByOptionalCost - this.state.patronCount) > 1 ? "s" : "") + " below or increase the number of patrons attending.";
        }

        const hasContactName = this.state.contactName && this.state.contactName.trim().length > 0;

        // Check if the user can submit the booking, patron name must be filled out, email must be valid, and terms and conditions must be agreed to
        const canSubmit = CommonPatronFunctions.patronNamesValid(this.state.patronNames, signup.requireAllPatronNames) && 
                          Email.validateEmail(this.state.patronEmail) && 
                          (!hasTerms || this.state.tcAgree) &&
                          patronCountMatch && hasContactName &&
                          (!signup.requiresMembership || this.state.membershipInfo.trim().length > 0);

        let width = window.innerWidth < 916 ? '100%' : '80%';
        if (window.innerWidth > 1200)
            width = 1160 * 0.8;       // leave 40 for margins

        const renderParams = {forPatron: true, 
                              onChange: this._additionalQuestionfieldChanged, 
                              onError: this._additionalQuestionfieldInputError};


        


        return (
            <Fragment>
                <div style={{display: this.state.showPayment ? 'none' : 'block', width: width, marginLeft: 'auto', marginRight: 'auto'}}>
                    {this.getConfirmAlertComponent()}

                    <Typography align='center' variant="h5" style={{marginTop: 20, marginBottom: 10}}>{"Event Signup: " + event.title}</Typography>
                    {CommonPatronFunctions.finalBookingSignupDateDisplay(event)}
                    <Typography align='center' variant="body1" style={{marginBottom: 10}}>{"Location: " + event.locationString}</Typography>

                    {this.state.multiDaySlaves ? 
                        CommonPatronFunctions.multidayAdditionalSessionInfo(this.state.event, this.state.multiDaySlaves)
                        : 
                        null
                    }
                    
                    <Typography align='center' variant='body1' style={{fontWeight: 'bold', marginBottom: 30, color: 'blue'}}>{remaininStr}</Typography>

                    <hr/>
                    <Typography variant="body1" style={{marginBottom: 10, color: 'red'}}>{msg}</Typography>

                    {hasAbout ?
                        <Fragment>
                            <div style={{marginTop: 20, marginBottom: 15}}>
                                <RichTextDisplay draftContent={signup.about}/>
                            </div>
                            <hr/>
                        </Fragment>
                        : 
                        <hr/>
                    }
                    <div style={{marginTop: 20}}/>

                    <Fragment>
                        <Typography variant="body1" style={{fontWeight: 'bold', color: 'blue', marginLeft: 10, marginTop: 30}}>{"Event spaces remaining: " + event.spacesLeft()}</Typography>
                        <Typography variant="body2" style={{fontStyle: 'italic', marginLeft: 10, marginTop: 10}}>How many Patrons attending this Event?</Typography>
                        
                        <div style={{display: 'flex', alignItems: 'center', marginTop: 5}}>
                            <div style={{maxWidth: 200, marginLeft: 10}}>
                                <ManageNumericField fullWidth={false} hasInfinity={false} justify='left' 
                                                    minValue={1}
                                                    maxValue={event.frontEndSpacesLeft}
                                                    commitTimeout={10}
                                                    autoAccept={true}
                                                    json="maxPatronCount"
                                                    initialValue={1} 
                                                    onFieldChange={this._patronCountChanged} changedBackgroundColor='white'/>  
                            </div>
                            {!patronCountMatch ?
                                <Typography variant="body2" style={{marginLeft: 10, fontStyle: 'italic', color: 'red'}}>{patronExpectedStr}</Typography>
                                : null
                            }
                        </div>
                    </Fragment>
                    
                    {signup.cost > 0 ?
                        CommonPatronFunctions.costItem(patronCost, "Patron Signup Fee", patronCostDescription) 
                        : null
                    }
                                                
                    {signup.optionalCosts.length > 0 ?
                        <Fragment>
                            <Typography variant="body2" style={{...this.styles.sectionLabel, marginTop: 10}}>{"Add Items"}</Typography>
                            <div style={this.styles.roundedContainer}>

                                {CommonPatronFunctions.renderOptionalCosts(signup.optionalCosts, this, "optionalCosts", signup.isoCurrency)}

                            </div>
                        </Fragment> 
                        : null
                    }

                    {totalServiceFees > 0 ?
                        <Fragment>
                            <Typography variant="body2" style={this.styles.sectionLabel}>{"Service Fees (" + signup.isoCurrency + ")"}</Typography>
                            <div style={this.styles.roundedContainer}>
                                {CommonPatronFunctions.costItem(totalServiceFees, "Service Fee", null)}      
                            </div>
                        </Fragment>
                        : null
                    }

                    {grandTotal > 0 ?
                        <Fragment>
                            <Typography align='center' variant="h5" style={{marginTop: 20, marginBottom: 10}}>{"Total Cost: " + Currency.round(grandTotal)}</Typography>
                            <hr/>
                        </Fragment>
                        : null
                    }
                    <Typography variant="subtitle1" style={{float: 'right', fontSize: 14, fontStyle: 'italic', color: ThemeColors.darkGray}}>* indicates required field</Typography>

                    <Typography variant="body2" style={{fontStyle: 'italic', fontWeight: 'bold', marginTop: 20}}>{"Who is Attending?" + (signup.requireAllPatronNames ? "" : " (Optional)")}</Typography>

                    {this.state.patronNames.map((patronName, index) =>
                        <TextField key={index} label={CommonPatronFunctions.patronNameLabel(index, this.state.patronNames.length, signup.requireAllPatronNames)} variant="outlined" 
                                value={patronName} fullWidth={true} size='small'
                                style={{marginTop: 15}}
                                InputLabelProps={{ shrink: true}}
                                    onChange={(event) => {
                                        const patronNames = [...this.state.patronNames];
                                        patronNames[index] = event.target.value;
                                        this.setState({patronNames: patronNames});
                                    }}/>
                    )}

                    <Typography variant="body2" style={{fontStyle: 'italic', fontWeight: 'bold', marginTop: 40}}>Your Information</Typography>

                    <ManageTextField label="Contact Name *"
                                    style={{marginTop: 15}}
                                    initialValue=""
                                    json="phone"  
                                    autoAccept={true}
                                    onFieldChange={this._setContactName}/>

                    <ManageTextField label="Contact Email Address *"
                                    style={{marginTop: 15}}
                                    initialValue=""
                                    json="email"  
                                    autoAccept={true}
                                    onFieldChange={this._setPatronEmail}/>

                    
                    <div style={{display: 'flex', alignItems: 'center', marginTop: 15}}>
                        <ManageTextField label="Contact Phone"
                                    style={{marginRight: 15, flexGrow: 1}}
                                    initialValue=""
                                    json="phone"  
                                    autoAccept={true}
                                    onFieldChange={this._setPatronPhone}/>
                    {/** COMING SOON ------------------------------------- 
                        <Checkbox color='primary' checked={this.state.enableSms} 
                                onChange={(event) => this.setState({enableSms: event.target.checked})}/>
                        <Typography variant="body2">Enable SMS Notifications</Typography>
                        */}
                    </div>

                    {signup.requiresMembership ?
                        <div style={{marginTop: 15}}>
                            <TextField size="small" variant="outlined" value={this.state.membershipInfo} label="Membership *"
                                        onChange={(event) => {this.setState({membershipInfo: event.target.value})}} 
                                        style={this.styles.membershipField} 
                                        fullWidth={true} InputLabelProps={{ shrink: true}} />
                            <Typography variant="body2" style={this.styles.membershipInstructionsLabel}>{"A valid Membership is required. Enter your pass number, barcode number, or Membership ID above."}</Typography>  
                        </div> : null
                    }

                    {signup.fieldTree && signup.fieldTree.children.length > 0 ?
                        <Fragment>
                            <hr style={{marginTop: 20}}/>
                           {renderField(signup.fieldTree, 0, renderParams, false)}

                        </Fragment> : null
                    }

                    {hasTerms ?
                        <div style={{marginTop: 30}}>
                            <div style={this.styles.roundedContainer}>
                                <RichTextDisplay draftContent={signup.termsAndConditions}/>
                                <div style={{display: 'flex', alignItems: 'center', marginBottom: -5, marginTop: 20}}>
                                    <Checkbox color='primary' checked={this.state.tcAgree} 
                                            onChange={(event) => this.setState({tcAgree: event.target.checked})}/>
                                    <Typography variant="body2">I agree to the Terms and Conditions</Typography>
                                </div>
                            </div>
                        </div>
                        : null
                    }

                    <div style={{display: 'flex', justifyContent: 'center', marginTop: 20, marginBottom: 40, gap: 30}}>
                        <Button disabled={!canSubmit} variant='contained' onClick={() => this._submit(grandTotal, false)} 
                                style={{backgroundColor: canSubmit ? ThemeColors.appBarBackground : ThemeColors.veryLightGray, color: 'white'}}>
                                    {grandTotal > 0 ? "Pay Now" : "Confirm Signup"}
                        </Button>
                        {signup.allowCashPayment && grandTotal > 0 ?
                            <Button disabled={!canSubmit} variant='contained' onClick={() => this._submit(grandTotal, true)} 
                                    style={{backgroundColor: canSubmit ? ThemeColors.appBarBackground : ThemeColors.veryLightGray, color: 'white'}}>
                                        Pay Later
                            </Button>
                            : null
                        }
                    </div>
        

                </div>
                {this.state.showPayment ? 
                    <PatronCheckout type="Signup"
                                    returnFromPayment={this._returnFromPayment} 
                                    paymentDescription={"Amount to Charge: " + Currency.round(grandTotal) + " " + signup.isoCurrency}
                                    purchaseComplete={() => this._gotoEventConfirmationPage(0)}
                                    clientSecret={this.state.clientSecret}
                                    email={this.state.patronEmail}/>
                    : null
                    }
            </Fragment>
        );
    }

}

export default withCookies(withRouter(EventSignupPage2));
